// Small helper utilities for AI builder draft caching.
// Canonical source of truth is the backend (Firestore) via /get_user_info/.
// We still mirror into localStorage for fast UX and cross-refresh continuity.

const LS_KEYS = {
  programDraft: "last_ai_plan_builder_cache",
  singleDraft: "last_ai_plan_builder_single_cache",
  programOngoing: "has_ongoing_ai_plan_builder_cache",
  singleOngoing: "has_ongoing_ai_plan_builder_single_cache",
};

function safeJsonParse(s, fallback = null) {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

function safeLsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
}

function inferIsOngoing(draft) {
  if (!draft || typeof draft !== "object") return false;
  return (draft.status || "in_progress") === "in_progress";
}

/**
 * Hydrate AI builder draft cache into localStorage from a user-info payload.
 *
 * Expected payload shape (backend):
 * - last_ai_plan_builder
 * - last_ai_plan_builder_single
 * - has_ongoing_ai_plan_builder (optional)
 * - has_ongoing_ai_plan_builder_single (optional)
 */
export function syncAiDraftCacheFromUserInfo(userInfo) {
  const ui = userInfo && typeof userInfo === "object" ? userInfo : {};

  const prog = ui.last_ai_plan_builder && typeof ui.last_ai_plan_builder === "object" ? ui.last_ai_plan_builder : null;
  const single =
    ui.last_ai_plan_builder_single && typeof ui.last_ai_plan_builder_single === "object"
      ? ui.last_ai_plan_builder_single
      : null;

  const progOngoing =
    typeof ui.has_ongoing_ai_plan_builder === "boolean" ? ui.has_ongoing_ai_plan_builder : inferIsOngoing(prog);
  const singleOngoing =
    typeof ui.has_ongoing_ai_plan_builder_single === "boolean"
      ? ui.has_ongoing_ai_plan_builder_single
      : inferIsOngoing(single);

  safeLsSet(LS_KEYS.programDraft, JSON.stringify(prog || null));
  safeLsSet(LS_KEYS.singleDraft, JSON.stringify(single || null));
  safeLsSet(LS_KEYS.programOngoing, JSON.stringify(Boolean(progOngoing)));
  safeLsSet(LS_KEYS.singleOngoing, JSON.stringify(Boolean(singleOngoing)));

  return { prog, single, progOngoing: Boolean(progOngoing), singleOngoing: Boolean(singleOngoing) };
}

export function readAiDraftCacheFromLocalStorage() {
  try {
    const prog = safeJsonParse(localStorage.getItem(LS_KEYS.programDraft) || "null", null);
    const single = safeJsonParse(localStorage.getItem(LS_KEYS.singleDraft) || "null", null);
    const progOngoing = safeJsonParse(localStorage.getItem(LS_KEYS.programOngoing) || "false", false);
    const singleOngoing = safeJsonParse(localStorage.getItem(LS_KEYS.singleOngoing) || "false", false);
    return {
      prog: prog && typeof prog === "object" ? prog : null,
      single: single && typeof single === "object" ? single : null,
      progOngoing: Boolean(progOngoing),
      singleOngoing: Boolean(singleOngoing),
    };
  } catch {
    return { prog: null, single: null, progOngoing: false, singleOngoing: false };
  }
}

export function clearAiDraftCacheLocalStorage() {
  try {
    safeLsSet(LS_KEYS.programDraft, JSON.stringify(null));
    safeLsSet(LS_KEYS.singleDraft, JSON.stringify(null));
    safeLsSet(LS_KEYS.programOngoing, JSON.stringify(false));
    safeLsSet(LS_KEYS.singleOngoing, JSON.stringify(false));
  } catch {
    // ignore
  }
}


